/**
 * Compute an RMS loudness level (0..1) from an AnalyserNode
 * `getByteTimeDomainData` buffer.
 *
 * Time-domain bytes are 0..255 centered on 128 (128 = silence). RMS of the
 * waveform amplitude is a far better loudness proxy than averaging frequency
 * bins: speech only excites a handful of FFT bins, so the bin-average barely
 * rises above silence, whereas the time-domain RMS tracks how loud the signal
 * actually is across the whole window.
 *
 * Pure and side-effect free so it can be unit tested without Web Audio.
 *
 * @param {Uint8Array|number[]} bytes getByteTimeDomainData buffer (0..255).
 * @returns {number} RMS amplitude clamped to [0, 1].
 */
export function rmsLevelFromTimeDomain(bytes) {
  if (!bytes || bytes.length === 0) return 0;

  let sumSquares = 0;
  for (let i = 0; i < bytes.length; i++) {
    // Map 0..255 (centered on 128) to a -1..1 sample amplitude.
    const sample = (bytes[i] - 128) / 128;
    sumSquares += sample * sample;
  }

  const rms = Math.sqrt(sumSquares / bytes.length);
  return Number.isFinite(rms) ? Math.min(1, Math.max(0, rms)) : 0;
}
